/* Tajweed colours on the muṣḥaf's own text.

   The Uthmani modes draw the King Fahd Complex (QPC) Ḥafṣ text with the
   KFGQPC Uthmanic Hafs font — the text that font was made for. Quran.com's
   tajweed markup (text_uthmani_tajweed) is in another encoding: other code
   points for sukūn and the staggered tanwīn, tatweel + small alif where QPC
   writes the small alif on the letter (ٱلرَّحۡمَـٰنِ / ٱلرَّحۡمَٰنِ), a hamza as a
   mark where QPC has ء, the small alif as U+0672 … Drawn as it is, it showed
   wrong marks (dotted circles, 2:72).

   So each rule is carried over to the text that is drawn by aligning the two
   letter by letter: a longest common subsequence on the letter skeleton,
   weighted so that matching vowel marks break ties (the right one of two
   tatweels pairs up). A rule whose letter has no partner — the tatweel that
   carried a small alif — goes to the letter that now carries those marks.
   A rule colours whole letters (letter + marks): a mark in a span of its own
   loses its letter in Safari. Where one letter would get two colours, its own
   rule wins over a madd, and a madd wins over colour that only spills onto the
   letter after an ikhfāʾ / idghām.

   Checked over the whole Qurʾān (tests/unit/quran-full.test.ts): the drawn
   text is always exactly the target text, and the result agrees with
   Quran.com's separate word-by-word markup on 99.99% of coloured letters.
   The output is ordinary tajweed markup, so every renderer keeps working. */
import { TAJWEED_CLASS_TO_KEY } from './fonts';

const MARK = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06DC\u06DF-\u06E8\u06EA-\u06ED\u08D3-\u08FF]/;
const IGNORE = /[\s\u00A0\u200C\u200D]/; // spaces and joiners aren't letters (tatweel is: it carries madd signs)
const TAG = /<(\/?)([a-z]+)([^>]*)>|([^<]+)/gi;
const CLASS = /class=["']?([\w-]+)/;
/* Quran.com's word-by-word markup splits the obligatory madd in two. */
const CANON: Record<string, string> = { madda_obligatory_mottasel: 'madda_obligatory', madda_obligatory_monfasel: 'madda_obligatory' };
/* The same mark across the encodings (QPC first): open tanwīn ٗ ٞ ٖ = ً ٌ ٍ,
   sukūn ۡ = ْ, the round "silent" sign ْ (QPC) = ۟, small alif ٲ = ٰ. */
const NORM: Record<string, string> = { '\u0657': '\u064B', '\u065E': '\u064C', '\u0656': '\u064D', '\u06E1': '\u0652', '\u06DF': '\u0652', '\u0672': '\u0670' };
const MADD = new Set(['madda_normal', 'madda_permissible', 'madda_obligatory', 'madda_necessary']);
/* Rules that colour the letter after the nūn/mīm too — a madd may take that letter. */
const SPILL = new Set(['ikhafa', 'ikhafa_shafawi', 'idgham_ghunnah', 'idgham_wo_ghunnah', 'idgham_shafawi', 'idgham_mutajanisayn', 'idgham_mutaqaribayn', 'iqlab']);
const TANWIN = /[\u064B-\u064D\u0656\u0657\u065E]/;

/** Letter skeleton: variants of the same letter compare equal. */
const skeleton = (c: string) =>
  /[\u0621-\u0623\u0625\u0627\u0671-\u0673\u0675]/.test(c) ? '\u0627'
    : /[\u0649\u064A\u066E\u06CC\u0626]/.test(c) ? '\u064A'
      : c === '\u0624' ? '\u0648' : c;

interface Cluster { text: string; base: string; cls?: string; word: number }

function clusters(chars: { ch: string; cls?: string; word?: number }[]): Cluster[] {
  const out: Cluster[] = [];
  for (const { ch, cls, word = 0 } of chars) {
    const last = out[out.length - 1];
    if (MARK.test(ch) && last && !IGNORE.test(last.base)) { last.text += ch; if (!last.cls && cls) last.cls = cls; continue; }
    out.push({ text: ch, base: ch, cls, word });
  }
  return out;
}

const marksOf = (c: Cluster) => new Set([...c.text.slice(1)].map((m) => NORM[m] ?? m));

/** Quran.com tajweed markup → characters with their rule (nested tags: the innermost known rule). */
function parseMarkup(html: string): { chars: { ch: string; cls?: string }[]; end: string | null } {
  const chars: { ch: string; cls?: string }[] = [];
  const stack: string[] = [];
  let end: string | null = null;
  for (const m of html.matchAll(TAG)) {
    const [, close, , attrs, text] = m;
    if (text != null) {
      if (stack[stack.length - 1] === '#end') { end = (end ?? '') + text; continue; }
      let cls: string | undefined;
      for (let i = stack.length - 1; i >= 0; i--) { const c = CANON[stack[i]] ?? stack[i]; if (TAJWEED_CLASS_TO_KEY[c]) { cls = c; break; } }
      for (const ch of text) chars.push({ ch: ch === '\u0672' ? '\u0670' : ch, cls });
    } else if (close) stack.pop();
    else { const c = CLASS.exec(attrs)?.[1] ?? ''; stack.push(c === 'end' ? '#end' : c); }
  }
  return { chars, end: end?.trim() || null };
}

/** Weighted LCS of two cluster lists (whitespace already removed) → index pairs. */
function alignPairs(U: Cluster[], T: Cluster[]): [number, number][] {
  const n = U.length, m = T.length, W = m + 1;
  const su = U.map((c) => skeleton(c.base)), st = T.map((c) => skeleton(c.base));
  const mu = U.map(marksOf), mt = T.map(marksOf);
  // A letter match scores 8, plus up to 4 for shared vowel marks.
  const score = (i: number, j: number) => {
    if (su[i] !== st[j]) return 0;
    const a = mu[i], b = mt[j];
    let both = 0;
    for (const x of a) if (b.has(x)) both++;
    const any = a.size + b.size - both;
    return 8 + (any ? Math.floor((4 * both) / any) : 4);
  };
  const dp = new Int32Array((n + 1) * W);
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) {
    const s = score(i, j);
    let best = Math.max(dp[(i + 1) * W + j], dp[i * W + j + 1]);
    if (s && dp[(i + 1) * W + j + 1] + s > best) best = dp[(i + 1) * W + j + 1] + s;
    dp[i * W + j] = best;
  }
  const pairs: [number, number][] = [];
  for (let i = 0, j = 0; i < n && j < m;) {
    const s = score(i, j);
    if (s && dp[i * W + j] === dp[(i + 1) * W + j + 1] + s) { pairs.push([i, j]); i++; j++; }
    else if (dp[i * W + j] === dp[(i + 1) * W + j]) i++;
    else j++;
  }
  return pairs;
}

/** May letter `u` take rule `cls` (it may already have one)? */
function canTake(u: Cluster, cls: string) {
  if (!u.cls || u.cls === cls) return true;
  return MADD.has(cls) && SPILL.has(u.cls) && u.base !== '\u0646' && u.base !== '\u0645' && !TANWIN.test(u.text);
}

/** Colour the target's clusters with the markup's rules; returns the āyah number from the markup. */
function colour(U: Cluster[], html: string): string | null {
  const { chars, end } = parseMarkup(html);
  const T = clusters(chars);
  const ui = U.map((c, i) => [c, i] as const).filter(([c]) => !IGNORE.test(c.base)).map(([, i]) => i);
  const ti = T.map((c, i) => [c, i] as const).filter(([c]) => !IGNORE.test(c.base)).map(([, i]) => i);
  const matched = new Map<number, number>(); // T index → U index
  for (const [a, b] of alignPairs(ui.map((i) => U[i]), ti.map((i) => T[i]))) {
    U[ui[a]].cls = T[ti[b]].cls;
    matched.set(ti[b], ui[a]);
  }
  // A rule on a letter with no partner (a tatweel carrying ـٰ / ـٰٓ / ـًٔ): the drawn
  // text writes those marks on a neighbouring letter — colour that one.
  ti.forEach((i, k) => {
    const cls = T[i].cls;
    if (!cls || matched.has(i)) return;
    let p: number | undefined, q: number | undefined;
    for (let j = k - 1; j >= 0 && p == null; j--) p = matched.get(ti[j]);
    for (let j = k + 1; j < ti.length && q == null; j++) q = matched.get(ti[j]);
    const free = ui.filter((u) => (p == null || u > p) && (q == null || u < q) && !U[u].cls);
    const want = marksOf(T[i]);
    let pick: number | undefined;
    if (free.length === 1) pick = free[0];
    else if (p != null && [...want].some((x) => marksOf(U[p!]).has(x))) pick = p;
    if (pick != null && canTake(U[pick], cls)) { U[pick].cls = cls; matched.set(i, pick); }
  });
  // A tanwīn the markup writes on the alif (مَثَل<…>اً م</…>) sits on the letter before it in the
  // drawn text (مَثَلٗا): the colour follows the tanwīn, so the sign itself is coloured too.
  for (const [t, u] of matched) {
    const cls = T[t].cls;
    if (!cls) continue;
    const mine = marksOf(U[u]);
    const lost = [...marksOf(T[t])].filter((x) => TANWIN.test(x) && !mine.has(x));
    if (!lost.length) continue;
    const k = ui.indexOf(u);
    const prev = k > 0 ? U[ui[k - 1]] : undefined;
    if (prev && lost.some((x) => marksOf(prev).has(x)) && canTake(prev, cls)) prev.cls = cls;
  }
  return end;
}

function emit(U: Cluster[]): string {
  let html = '';
  for (let i = 0; i < U.length;) {
    const cls = U[i].cls;
    if (!cls || IGNORE.test(U[i].base)) { html += U[i++].text; continue; } // a space inside a coloured stretch stays uncoloured
    let run = '';
    while (i < U.length && U[i].cls === cls && !IGNORE.test(U[i].base)) run += U[i++].text;
    html += `<tajweed class=${cls}>${run}</tajweed>`;
  }
  return html;
}

const cache = new Map<string, { body: string; end: string | null }>();
const wordCache = new Map<string, { words: string[]; end: string | null }>();

/** Tajweed markup whose text is exactly `target`, coloured per `tajweedHtml` — with the
    markup's āyah number at the end unless `withNumber` is false. */
export function alignTajweed(target: string, tajweedHtml: string, withNumber = true): string {
  const key = target + '\u0000' + tajweedHtml;
  let hit = cache.get(key);
  if (!hit) {
    const U = clusters([...target].map((ch) => ({ ch })));
    const end = colour(U, tajweedHtml);
    hit = { body: emit(U), end };
    cache.set(key, hit);
  }
  return hit.body + (withNumber && hit.end ? ` <span class=end>${hit.end}</span>` : '');
}

/** The same, word by word: one markup string per word (for word highlighting). */
export function alignTajweedWords(words: string[], tajweedHtml: string): { words: string[]; end: string | null } {
  const key = words.join('\u0001') + '\u0000' + tajweedHtml;
  const hit = wordCache.get(key);
  if (hit) return hit;
  const chars: { ch: string; word: number }[] = [];
  words.forEach((w, n) => { if (n) chars.push({ ch: ' ', word: -1 }); for (const ch of w) chars.push({ ch, word: n }); });
  const U = clusters(chars);
  const end = colour(U, tajweedHtml);
  const out = { words: words.map((_, n) => emit(U.filter((c) => c.word === n))), end };
  wordCache.set(key, out);
  return out;
}
